<script>
  // Step 1 body: Set Page Scale. Renders inside a <StepCard>; the parent
  // panel owns the card's number/title/subline/done/warning.
  import { createEventDispatcher, getContext } from "svelte";
  import cloneDeep from "lodash/cloneDeep";

  import { Modal } from "svelte-utilities";
  import SelectInput from "#src/lib/sidebar/SelectInput.svelte";
  import CustomScaleModal from "#src/lib/sidebar/CustomScaleModal.svelte";
  import { scaleOptions } from "@local/extensions/utilities/scales.js";
  import { locRecord } from "#src/extensions/filter-items.js";
  import analyzePages from "#src/extensions/analyze-pages.js";
  import { pageAnalysisLlmModel, pageAnalysisReasoningLevel, currentTool } from "#src/stores/ui.js";
  import { profile } from "#src/stores/auth.js";
  import { isNotApplicable } from "../derive.js";
  import { plural } from "../labels.js";
  import { setPageFlags } from "../page-flags.js";
  import { forceScaleFor } from "../force-scale.js";

  export let group;
  export let doc;
  export let pages;
  export let indexedLocations;
  export let selectedPageList;

  const dispatch = createEventDispatcher();
  const org = getContext("org");

  let detecting = false;
  let scaleModal;
  let customScaleModal;
  let scaleValue = null;
  let forcedNote = "";
  let consumedForcePageId = null;
  // True while the scale modal is open because a frame placement was blocked
  // on an unscaled page — set below, used to re-arm the frame tool on confirm.
  let forced = false;
  let pendingForcedOpen = false;

  // Something needs this page scaled before it can proceed: a frame placement
  // (Document.svelte's shpx+framex-gated hook, which aborts before any frame is
  // created) or Find Frames pressed with an unscaled page selected. Auto-open the
  // same modal the "Set page scale manually" link opens, with a note explaining
  // why. Keyed on pageId so re-renders don't reopen a modal the user already
  // dismissed; the Modal's on:close clears that key, so a later press can reopen it.
  $: if ($forceScaleFor) {
    const { pageId, note } = $forceScaleFor;
    if (pageId !== consumedForcePageId) {
      consumedForcePageId = pageId;
      forced = true;
      // Placement is the default wording; other callers pass their own.
      forcedNote = note ?? "This page needs a scale before you can place a frame on it.";
      forceScaleFor.set(null);
      pendingForcedOpen = true;
    }
  }

  // The block above can run during Step 1's initial mount (the forced flow
  // expands Step 1, mounting this component), before the Modal's bind:this
  // has set `scaleModal`. Defer the open until scaleModal exists so we never
  // call .open() on undefined.
  $: if (pendingForcedOpen && scaleModal) {
    pendingForcedOpen = false;
    openScaleModal();
  }

  $: hasSelection = selectedPageList.length > 0;

  async function detectScale() {
    detecting = true;
    const data = await analyzePages(
      selectedPageList,
      doc,
      $org.id,
      $pageAnalysisLlmModel,
      $pageAnalysisReasoningLevel,
      $profile?.user_role === "developer",
    );

    if (data) {
      group.updateDocument(doc.id, { data });
    }

    detecting = false;
  }

  function openScaleModal() {
    // Preload the dropdown with what the selection already is, when every selected
    // sheet agrees; otherwise leave it on "None" rather than guessing.
    //
    // N/A is tested first and separately because it is not a scale: an N/A page
    // carries `scale: null` alongside the notApplicable flag, so reading `scale`
    // alone reported it as "None" and the dropdown could never show the state the
    // user had set. A selection that disagrees about N/A falls through to the scale
    // test, which is the same "don't guess" rule.
    if (selectedPageList.length && selectedPageList.every((p) => isNotApplicable(pages[p.id]))) {
      scaleValue = "__na__";
    } else {
      const scales = new Set(selectedPageList.map((p) => pages[p.id]?.scale ?? null));
      scaleValue = scales.size === 1 ? [...scales][0] : null;
    }
    scaleModal.open();
  }

  function applyScale() {
    const pageIds = selectedPageList.map((p) => p.id);

    if (scaleValue === "__na__") {
      // N/A means "not a scaled drawing": clear any scale and mark the
      // pages skipped-on-purpose so the canvas flag reads grey N/A and
      // membership (isInFrameX) drops them.
      setPageScale(null);
      setPageFlags(group, doc, pageIds, { notApplicable: true });
    } else {
      setPageScale(scaleValue);
      // Unconditional, "None" included. None means "no scale yet", which is a
      // different state from "not a scaled drawing" — so choosing it has to return
      // the page to NO SCALE. Guarding this on a non-null scale made N/A a one-way
      // door: the page kept reading N/A and stayed out of FrameX whatever the
      // dropdown was changed to.
      setPageFlags(group, doc, pageIds, { notApplicable: false });
    }

    // If the modal was forced open by a blocked placement and a real scale
    // was set, re-arm the frame tool so the user can place the frame right
    // away — no need to re-click the tool button.
    if (forced && scaleValue !== "__na__" && scaleValue !== null) {
      $currentTool = "location-collection";
      dispatch("click-location-tool");
    }
    forced = false;

    scaleModal.close();
  }

  /**
   * Seeds drawing_scale onto every frame on the sheet that doesn't already
   * have one (an override stays put), and keeps an item's drawing.clip in
   * sync when the location is that item's active attachment. Also used to
   * clear a scale — called with `value: null` — which is why it never
   * touches notApplicable.
   */
  function setPageScale(value) {
    const pageIds = selectedPageList.map((p) => p.id);

    const data = cloneDeep(doc.data);
    pageIds.forEach((pageId) => {
      data.pages[pageId].scale = value;
    });

    const updates = [{ type: "document", id: doc.id, diff: { data } }];

    pageIds.forEach((pageId) => {
      const locs = indexedLocations[pageId] || [];

      locs.forEach((loc) => {
        if (loc.clip && !loc.clip.drawing_scale) {
          const clip = { ...loc.clip, drawing_scale: value };
          updates.push({
            type: "location",
            id: loc.id,
            diff: { clip },
          });

          const record = locRecord(loc, $group);
          if (record?.drawing?.attachment_id === loc.id) {
            const drawing = cloneDeep(record.drawing);
            drawing.clip = clip;
            updates.push({
              type: loc.record_type === "collection" ? "item" : loc.record_type,
              id: record.id,
              diff: { drawing },
            });
          }
        }
      });
    });

    group.update(updates);
  }

  function pickDimension() {
    scaleModal.close();
    dispatch("pick-dimension");
  }
</script>

<div class="fx-actions">
  <!-- Setting the scale by hand is the primary path and carries its own
       scope; auto-detect drops to the secondary because it isn't reliable
       enough yet to be the first thing a new user reaches for.

       With nothing selected both buttons stay rendered but disabled, and the
       primary states the prerequisite instead of naming an action it will not
       perform — the same treatment step 2 gives Find Frames, so the two steps
       answer "nothing is selected" the same way. -->
  <button class="fx-primary-button" disabled={detecting || !hasSelection} on:click={openScaleModal}>
    {hasSelection
      ? `Set scale for (${selectedPageList.length}) selected page${plural(selectedPageList.length)}`
      : "Select pages to set scales"}
  </button>
  <button class="fx-secondary-button" disabled={detecting || !hasSelection} on:click={detectScale}>
    {detecting ? "Detecting…" : "Try auto-detect"}
  </button>
  {#if hasSelection}
    <div class="fx-hint">
      Auto-detect reads the title block and drawing to identify scale, name, and number.
    </div>
  {/if}
</div>

<Modal
  bind:this={scaleModal}
  closeable
  on:confirm={applyScale}
  on:close={() => {
    forcedNote = "";
    forced = false;
    consumedForcePageId = null;
  }}
  buttons={[
    { label: "Cancel", type: "cancel" },
    { label: "Set Scale", type: "confirm", style: "primary" },
  ]}>
  <div slot="title">Set Page Scale</div>
  <div slot="content" class="space-y-4">
    {#if forcedNote}
      <p class="fx-forced-note">{forcedNote}</p>
    {/if}
    <div class="space-y-2">
      <p class="text-sm">
        Setting scale for {selectedPageList.length} selected page{plural(selectedPageList.length)}.
      </p>
      <SelectInput
        border
        label="Drawing Scale"
        options={[
          { label: "None", value: null },
          { label: "N/A — not a scaled drawing", value: "__na__" },
          { label: "Set Custom Scale", value: "custom", unclickable: true },
          ...scaleOptions,
          ...(scaleValue && scaleValue !== "__na__" && !scaleOptions.some((o) => o.value === scaleValue)
            ? [{ label: `Custom Scale: ${scaleValue}`, value: scaleValue }]
            : []),
        ]}
        bind:value={scaleValue}
        on:option-click={() => customScaleModal.open()} />
    </div>
    <div class="text-sm">
      No visible scale? <button class="btn-text" on:click={pickDimension}>Pick a dimension</button>
    </div>
  </div>
</Modal>
<CustomScaleModal bind:this={customScaleModal} on:setScale={(e) => (scaleValue = e.detail.value)} />

<style lang="scss">
  .fx-forced-note {
    background: var(--fx-amber-bg);
    border: 1px solid var(--fx-amber-border);
    color: var(--fx-amber-text);
    font-size: 12px;
    font-weight: 600;
    padding: 8px 9px;
    border-radius: 6px;
    line-height: 1.4;
  }
</style>
